import { useState, useEffect } from "react";
import axios from "axios";
import { Copy, LogOut, Mail, Package, Users, ShieldCheck, Sparkles, Phone, BookOpen, Download, Clock } from "lucide-react";
import { SEO, API } from "./components";

const STORAGE_KEY = "sf_admin_pwd";

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.get(`${API}/orders?password=${encodeURIComponent(password)}`);
      localStorage.setItem(STORAGE_KEY, password);
      onLogin(password);
    } catch (e) {
      setError("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <SEO title="Dawn — Admin" description="Private" />
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <ShieldCheck size={48} className="text-[#F5D48A]" />
          </div>
          <h1 className="serif text-4xl gold-shimmer mb-2">Dawn's Chair</h1>
          <p className="text-[#A89FA8] text-sm tracking-widest uppercase">Private Admin</p>
        </div>
        <form onSubmit={submit} className="sf-card jewel-card jewel-gold rounded-sm p-8">
          <label className="form-label">Password</label>
          <input
            type="password"
            required
            autoFocus
            data-testid="admin-password"
            className="form-input mb-4"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <p className="text-rose-300 text-sm mb-4" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-gold w-full">
            {loading ? "Checking..." : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button onClick={copy} className="text-[#F5D48A] hover:text-white p-1" title="Copy">
      {copied ? <Sparkles size={14} /> : <Copy size={14} />}
    </button>
  );
}

function OrdersPanel({ password }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/orders?password=${encodeURIComponent(password)}`)
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, [password]);

  const revenue = orders.reduce((sum, o) => sum + o.price, 0);

  return (
    <div className="sf-card rounded-sm p-6 mb-8" data-testid="admin-orders-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="text-[#F5D48A]" size={22} />
          <h2 className="serif text-2xl text-[#F2EFE8]">Orders</h2>
          <span className="text-xs tracking-widest uppercase text-[#A89FA8]">{orders.length} total</span>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest uppercase text-[#A89FA8]">Revenue Interest</div>
          <div className="serif text-2xl gold-shimmer" data-testid="admin-revenue">${revenue.toFixed(2)}</div>
        </div>
      </div>
      {loading ? (
        <p className="text-[#A89FA8]">Loading...</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-12">
          <Sparkles className="mx-auto text-[#B58B2E] mb-3" size={28} />
          <p className="text-[#A89FA8]">No orders yet — but they're coming.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[#B58B2E] border-b border-[#B58B2E]/25">
                <th className="pb-3">Date</th>
                <th className="pb-3">Name</th>
                <th className="pb-3">Email</th>
                <th className="pb-3">Product</th>
                <th className="pb-3 text-right">Price</th>
                <th className="pb-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="text-[#F2EFE8]/85">
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#B58B2E]/10 hover:bg-[#4B1D6D]/20" data-testid={`admin-order-${o.id}`}>
                  <td className="py-3 text-xs text-[#A89FA8]">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="py-3 font-medium">{o.customer_name}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <a href={`mailto:${o.customer_email}?subject=Your Mirror Lotus Deck from Dawn`} className="text-[#F5D48A] hover:underline text-xs">{o.customer_email}</a>
                      <CopyButton text={o.customer_email} />
                    </div>
                  </td>
                  <td className="py-3 text-xs">{o.product_name}</td>
                  <td className="py-3 text-right serif text-lg text-[#F5D48A]">${o.price}</td>
                  <td className="py-3 text-center">
                    <span className="text-[10px] tracking-wider uppercase px-2 py-1 rounded bg-[#B58B2E]/20 text-[#F5D48A]">
                      {o.status === "pending_preview" ? "Preview" : o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CardOrdersPanel({ password }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/card-orders?password=${encodeURIComponent(password)}`)
      .then((r) => setOrders(r.data))
      .finally(() => setLoading(false));
  }, [password]);

  return (
    <div className="sf-card rounded-sm p-6 mb-8" data-testid="admin-card-orders-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="text-[#F5D48A]" size={22} />
          <h2 className="serif text-2xl text-[#F2EFE8]">Mirror Lotus Card Print Orders</h2>
          <span className="text-xs tracking-widest uppercase text-[#A89FA8]">{orders.length} total</span>
        </div>
      </div>
      {loading ? (
        <p className="text-[#A89FA8]">Loading...</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-10">
          <Sparkles className="mx-auto text-[#B58B2E] mb-3" size={28} />
          <p className="text-[#A89FA8]">No card print purchases yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[#B58B2E] border-b border-[#B58B2E]/25">
                <th className="pb-3">When</th>
                <th className="pb-3">Card</th>
                <th className="pb-3">Customer</th>
                <th className="pb-3">Dedication</th>
                <th className="pb-3">Download Code</th>
              </tr>
            </thead>
            <tbody className="text-[#F2EFE8]/85">
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#B58B2E]/10 hover:bg-[#4B1D6D]/20 align-top">
                  <td className="py-3 text-xs text-[#A89FA8] whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="py-3 font-medium">{o.card_name}</td>
                  <td className="py-3 text-xs">
                    {o.customer_name}<br/>
                    <a href={`mailto:${o.customer_email}`} className="text-[#F5D48A] hover:underline">{o.customer_email}</a>
                  </td>
                  <td className="py-3 text-xs italic max-w-xs">{o.dedication || <span className="text-[#A89FA8]">—</span>}</td>
                  <td className="py-3 text-xs font-mono text-[#F5D48A]">
                    <div className="flex items-center gap-2">
                      <span>{o.download_token}</span>
                      <CopyButton text={o.download_token} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const SESSION_STATUSES = ["new", "scheduled", "done"];

function PaidSessionsPanel({ password }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/clarity-sessions?password=${encodeURIComponent(password)}`)
      .then((r) => setSessions(r.data))
      .finally(() => setLoading(false));
  }, [password]);

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/clarity-sessions/${id}?password=${encodeURIComponent(password)}`, { status });
    setSessions((list) => list.map((s) => (s.id === id ? { ...s, status } : s)));
  };

  const total = sessions.reduce((sum, s) => sum + (s.price || 0), 0);

  return (
    <div className="sf-card rounded-sm p-6 mb-8" data-testid="admin-paid-sessions-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Clock className="text-[#F5D48A]" size={22} />
          <h2 className="serif text-2xl text-[#F2EFE8]">Paid Clarity Sessions · $1/min</h2>
          <span className="text-xs tracking-widest uppercase text-[#A89FA8]">{sessions.length} total</span>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-widest uppercase text-[#A89FA8]">Prepaid</div>
          <div className="serif text-2xl gold-shimmer" data-testid="admin-paid-sessions-total">${total.toFixed(2)}</div>
        </div>
      </div>
      {loading ? (
        <p className="text-[#A89FA8]">Loading...</p>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12">
          <Sparkles className="mx-auto text-[#B58B2E] mb-3" size={28} />
          <p className="text-[#A89FA8]">No paid sessions yet — they&apos;ll appear here the moment someone prepays.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-paid-sessions-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[#B58B2E] border-b border-[#B58B2E]/25">
                <th className="pb-3">When</th>
                <th className="pb-3">Customer</th>
                <th className="pb-3 text-right">Minutes</th>
                <th className="pb-3 text-right">Paid</th>
                <th className="pb-3">Preferred Time</th>
                <th className="pb-3">On Their Heart</th>
                <th className="pb-3">Code</th>
                <th className="pb-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="text-[#F2EFE8]/85">
              {sessions.map((s) => (
                <tr key={s.id} className="border-b border-[#B58B2E]/10 hover:bg-[#4B1D6D]/20 align-top" data-testid={`admin-paid-session-${s.id}`}>
                  <td className="py-3 text-xs text-[#A89FA8] whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                  <td className="py-3 text-xs">
                    <div className="font-medium text-sm">{s.customer_name}</div>
                    <div className="flex items-center gap-2">
                      <a href={`mailto:${s.customer_email}?subject=Your ${s.minutes}-Minute Clarity Session with Dawn`} className="text-[#F5D48A] hover:underline">{s.customer_email}</a>
                      <CopyButton text={s.customer_email} />
                    </div>
                  </td>
                  <td className="py-3 text-right">{s.minutes}</td>
                  <td className="py-3 text-right serif text-lg text-[#F5D48A]">${s.price}</td>
                  <td className="py-3 text-xs max-w-[180px]">{s.preferred_time}</td>
                  <td className="py-3 text-xs max-w-xs">{s.note || <span className="text-[#A89FA8] italic">—</span>}</td>
                  <td className="py-3 text-xs font-mono text-[#F5D48A] whitespace-nowrap">{s.confirmation_code}</td>
                  <td className="py-3">
                    <div className="flex gap-1 justify-center">
                      {SESSION_STATUSES.map((st) => (
                        <button
                          key={st}
                          onClick={() => setStatus(s.id, st)}
                          data-testid={`admin-paid-session-${s.id}-status-${st}`}
                          className={`text-[10px] tracking-wider uppercase px-2 py-1 rounded border transition-colors ${
                            s.status === st
                              ? "bg-[#B58B2E]/30 border-[#F5D48A] text-[#F5D48A]"
                              : "border-[#B58B2E]/20 text-[#A89FA8] hover:text-[#F5D48A]"
                          }`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClarityCallsPanel({ password }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/clarity-calls?password=${encodeURIComponent(password)}`)
      .then((r) => setCalls(r.data))
      .finally(() => setLoading(false));
  }, [password]);

  return (
    <div className="sf-card rounded-sm p-6 mb-8" data-testid="admin-clarity-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Phone className="text-[#F5D48A]" size={22} />
          <h2 className="serif text-2xl text-[#F2EFE8]">Free Clarity Call Requests</h2>
          <span className="text-xs tracking-widest uppercase text-[#A89FA8]">{calls.length} total</span>
        </div>
      </div>
      {loading ? (
        <p className="text-[#A89FA8]">Loading...</p>
      ) : calls.length === 0 ? (
        <div className="text-center py-12">
          <Sparkles className="mx-auto text-[#B58B2E] mb-3" size={28} />
          <p className="text-[#A89FA8]">No call requests yet — they&apos;ll appear here the moment someone books.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-clarity-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[#B58B2E] border-b border-[#B58B2E]/25">
                <th className="pb-3">When</th>
                <th className="pb-3">Name</th>
                <th className="pb-3">Email</th>
                <th className="pb-3">Phone</th>
                <th className="pb-3">On Their Mind</th>
                <th className="pb-3">Source</th>
              </tr>
            </thead>
            <tbody className="text-[#F2EFE8]/85">
              {calls.map((c) => (
                <tr key={c.id} className="border-b border-[#B58B2E]/10 hover:bg-[#4B1D6D]/20 align-top" data-testid={`admin-clarity-${c.id}`}>
                  <td className="py-3 text-xs text-[#A89FA8] whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                  <td className="py-3 font-medium">{c.name}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <a href={`mailto:${c.email}?subject=Your Complimentary Clarity Call`} className="text-[#F5D48A] hover:underline text-xs">{c.email}</a>
                      <CopyButton text={c.email} />
                    </div>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <a href={`tel:${c.phone}`} className="text-[#F5D48A] hover:underline text-xs">{c.phone}</a>
                      <CopyButton text={c.phone} />
                    </div>
                  </td>
                  <td className="py-3 text-xs max-w-xs">{c.message || <span className="text-[#A89FA8] italic">—</span>}</td>
                  <td className="py-3 text-[10px] tracking-wider uppercase text-[#A89FA8]">{c.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SubscribersPanel({ password }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/subscribers?password=${encodeURIComponent(password)}`)
      .then((r) => setSubs(r.data))
      .finally(() => setLoading(false));
  }, [password]);

  const copyAllEmails = () => {
    const all = subs.map((s) => s.email).join(", ");
    navigator.clipboard.writeText(all);
    alert(`Copied ${subs.length} emails to clipboard.`);
  };

  return (
    <div className="sf-card rounded-sm p-6" data-testid="admin-subscribers-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="text-[#F5D48A]" size={22} />
          <h2 className="serif text-2xl text-[#F2EFE8]">Friday Mirror Subscribers</h2>
          <span className="text-xs tracking-widest uppercase text-[#A89FA8]">{subs.length} on the list</span>
        </div>
        {subs.length > 0 && (
          <button onClick={copyAllEmails} className="btn-outline-gold text-xs" data-testid="admin-copy-all">Copy All Emails</button>
        )}
      </div>
      {loading ? (
        <p className="text-[#A89FA8]">Loading...</p>
      ) : subs.length === 0 ? (
        <div className="text-center py-12">
          <Mail className="mx-auto text-[#B58B2E] mb-3" size={28} />
          <p className="text-[#A89FA8]">No subscribers yet. The Friday Mirror awaits.</p>
        </div>
      ) : (
        <div className="grid gap-2 max-h-96 overflow-y-auto">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded bg-[#4B1D6D]/20 hover:bg-[#4B1D6D]/40 transition-colors" data-testid={`admin-sub-${s.id}`}>
              <div className="flex items-center gap-3">
                <Sparkles size={14} className="text-[#B58B2E] flex-shrink-0" />
                <a href={`mailto:${s.email}`} className="text-[#F5D48A] hover:underline text-sm">{s.email}</a>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-[#A89FA8]">{new Date(s.created_at).toLocaleDateString()}</span>
                <CopyButton text={s.email} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      axios.get(`${API}/orders?password=${encodeURIComponent(saved)}`)
        .then(() => { setPassword(saved); setAuthed(true); })
        .catch(() => localStorage.removeItem(STORAGE_KEY));
    }
  }, []);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
  };

  if (!authed) return <LoginScreen onLogin={(p) => { setPassword(p); setAuthed(true); }} />;

  return (
    <div className="pt-32 pb-16 px-6 lg:px-12 min-h-screen" data-testid="admin-page">
      <SEO title="Dawn — Admin" description="Private admin dashboard" />
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="section-label" data-testid="admin-label">Private</div>
            <h1 className="display-serif text-5xl sm:text-6xl mb-2" data-testid="admin-heading">Dawn's Chair</h1>
            <p className="text-[#F2EFE8]/60 text-sm">Every order. Every subscriber. All in one place.</p>
          </div>
          <button onClick={logout} className="btn-outline-gold flex items-center gap-2 text-xs" data-testid="admin-logout">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
        <OrdersPanel password={password} />
        <CardOrdersPanel password={password} />
        <PaidSessionsPanel password={password} />
        <ClarityCallsPanel password={password} />
        <SubscribersPanel password={password} />
        <PrintKitPanel />
      </div>
    </div>
  );
}

function PrintKitPanel() {
  return (
    <div className="sf-card jewel-card jewel-gold rounded-sm p-6 mb-8" data-testid="admin-print-kit">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-[#F5D48A]" />
          <h2 className="serif text-2xl text-[#F2EFE8]">Amazon Print Kit — Love Connection Workbook</h2>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-[#B58B2E]">Draft · Not Published</span>
      </div>
      <p className="text-[#F2EFE8]/70 text-sm mb-5 leading-relaxed">
        Complete Amazon KDP paperback set — your original wording, purple &amp; gold branding.
        The <em>interior</em> is a 52-page 7&quot; × 10&quot; workbook with title page, dedication, three petal chapters,
        18 prompts with generous writing space, &ldquo;Look for Your Loops&rdquo; recap, and a final invitation to <em>snapshotfame.com</em>.
        The <em>cover</em> is a print-ready wrap (back · spine · front) sized 14.37&quot; × 10.25&quot; with 0.117&quot; spine, bleed, and an ISBN barcode area.
        Review both first — nothing has been uploaded to Amazon.
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        <a
          href={`${API}/exports/love-connection-workbook.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold flex items-center justify-center gap-2"
          data-testid="download-workbook-pdf"
        >
          <Download size={14} /> Interior PDF (52 pp · 7×10&quot;)
        </a>
        <a
          href={`${API}/exports/love-connection-cover.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold flex items-center justify-center gap-2"
          data-testid="download-cover-pdf"
        >
          <Download size={14} /> Full Cover PDF (14.37×10.25&quot;)
        </a>
        <a
          href={`${API}/exports/love-connection-workbook.docx`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline-gold flex items-center justify-center gap-2"
          data-testid="download-workbook-docx"
        >
          <Download size={14} /> Editable Manuscript (Word .docx)
        </a>
      </div>
      <p className="text-[10px] text-[#A89FA8] mt-4 tracking-widest uppercase">
        Preview only · Publish to Amazon KDP after your review 🌸
      </p>
    </div>
  );
}
